import { Link } from 'react-router-dom'
import { MapPin, CheckCircle, Clock } from 'lucide-react'

import { Alert } from '@/components/ui/Alert'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatDistance, useTodaysRoute } from '@/features/field_routes/api'
import { useVisits } from '@/features/field_visits/api'

/**
 * Landing tab for `/m`: the day at a glance.
 *
 * The counts come from the visits, not the route, on purpose — a walk-in the rep recorded
 * off-plan still counts towards their day. The route card is the planned round and links
 * through to `/m/route`, which is where the map and the driving order live.
 */
export function MobileHome() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const { data: visitsData, isLoading, error } = useVisits({
    date_from: today.toISOString(),
  })
  const { data: route } = useTodaysRoute()

  const visits = visitsData?.data || []

  const completedCount = visits.filter(v => v.status === 'completed').length
  const pendingCount = visits.filter(v => v.status === 'planned' || v.status === 'in_progress').length
  const ordersCount = visits.filter(v => v.order_id).length

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{pendingCount}</div>
          <div className="text-xs font-medium text-slate-500">Pending</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="text-2xl font-bold text-green-600 dark:text-green-500">{completedCount}</div>
          <div className="text-xs font-medium text-slate-500">Completed</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="text-2xl font-bold text-brand-600 dark:text-brand-500">{ordersCount}</div>
          <div className="text-xs font-medium text-slate-500">Orders</div>
        </div>
      </div>

      {route && (
        <Link
          to="/m/route"
          className="block rounded-lg border border-brand-200 bg-brand-50 p-4 dark:border-brand-900/60 dark:bg-brand-950/40"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-brand-900 dark:text-brand-100">
                Today&rsquo;s round · {route.stops.length} stops
              </p>
              <p className="mt-0.5 text-xs text-brand-700 dark:text-brand-300">
                {formatDistance(route.total_distance_m)} of driving
                {route.distance_provider === 'haversine' && ' (estimated)'}
              </p>
            </div>
            <MapPin className="h-5 w-5 text-brand-600 dark:text-brand-400" aria-hidden="true" />
          </div>
        </Link>
      )}

      <Card>
        <CardHeader
          title="Today's Route"
          description="Your scheduled visits for the day."
        />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-4 text-sm text-slate-500">Loading visits...</div>
          ) : error ? (
            <div className="p-4">
              <Alert tone="error">Could not load today&rsquo;s visits. Check the connection and try again.</Alert>
            </div>
          ) : visits.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No visits scheduled for today.</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {visits.map((visit) => (
                <Link
                  key={visit.id}
                  to={`/m/visits/${visit.id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <div>
                    <div className="font-medium text-slate-900 dark:text-white">
                      Visit {visit.visit_number}
                    </div>
                    <div className="mt-1 flex items-center text-xs text-slate-500">
                      {visit.status === 'completed' ? (
                        <CheckCircle className="mr-1 h-3 w-3 text-green-500" />
                      ) : visit.status === 'in_progress' ? (
                        <Clock className="mr-1 h-3 w-3 text-amber-500" />
                      ) : (
                        <MapPin className="mr-1 h-3 w-3 text-slate-400" />
                      )}
                      {visit.status.replace('_', ' ')}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Link
          to="/m/device-check"
          className="block rounded-lg border border-slate-200 bg-white p-4 text-sm font-medium text-brand-600 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-brand-400 dark:hover:bg-slate-800"
        >
          Device check — GPS and camera
        </Link>

        <Link
          to="/m/leads/new"
          className="block rounded-lg border border-slate-200 bg-white p-4 text-sm font-medium text-brand-600 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-brand-400 dark:hover:bg-slate-800"
        >
          Capture Lead
        </Link>
      </div>
    </div>
  )
}
